import {
  IAM_CONTRACT,
  MAX_IAM_SHIFT_LENGTH_MINUTES,
  MAX_TWU_SHIFT_LENGTH_MINUTES,
  MIN_IAM_SHIFT_LENGTH_MINUTES,
} from "../constants";
import { getTimeDifferenceInMinutes } from "../dateHelper";
import { setEndDateTime, setStartDateTime } from "../inputValidation";
import {
  END_TIME_IS_REQUIRED,
  MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE,
  MAX_SHIFT_10_AND_ONE_HALF_HOURS_WARNING_MESSAGE,
  MIN_SHIFT_30_MINUTES_WARNING_MESSAGE,
  START_TIME_IS_REQUIRED,
} from "../messageConstants";
import { getTimeZoneFromSwaStation } from "../timeZoneService";
import {
  AT_LEAST_ONE_PREFERENCE_MUST_BE_SELECTED_ERROR,
  AT_LEAST_ONE_SHIFT_IN_FUTURE_ERROR,
  MIN_TWU_SHIFT_LENGTH_MINUTES,
  MUST_SELECT_AT_LEAST_ONE_HOURS_PREFERENCE_ERROR,
  MUST_SELECT_AT_LEAST_ONE_TIME_OF_DAY_PREFERENCE_ERROR,
  PREFERENCE_SELECTED_DUPLICATION_ERROR,
  START_END_TIME_IS_REQUIRED_ERROR,
} from "./constants";
import { DateTime } from "luxon";

export function validateShiftSegments(segments, date, station, contract) {
  const validationFns = [
    validateShiftSegmentComplete,
    validateShiftStartSegment,
    validateShiftEndSegment,
    validateShiftLength,
  ];

  let isFormValid = true;
  segments.forEach((currentSegment) => {
    currentSegment.shiftSegmentError = "";
    for (const validateFn of validationFns) {
      const validationError = validateFn(currentSegment, date, station, contract);
      currentSegment.shiftSegmentError = validationError;
      if (validationError !== "") {
        isFormValid = false;
        return;
      }
    }
  });

  if (isFormValid) {
    const validationError = validateAtLeastOneShiftEndingInFuture(segments, date, station);
    segments.forEach((currentSegment) => {
      currentSegment.shiftSegmentError = validationError;
    });

    if (validationError !== "") {
      isFormValid = false;
    }
  }

  return { segments, isFormValid };
}

function validateAtLeastOneShiftEndingInFuture(segments, date, station) {
  const now = DateTime.now().setZone(getTimeZoneFromSwaStation(station));

  const isAtLeastOneShiftInFutureEnd = segments.some((current) => {
    const startDateTimeVal = date
      .set({ hour: current.shiftStartHr, minute: current.shiftStartMin, second: 0, millisecond: 0 })
      .setZone(getTimeZoneFromSwaStation(station), { keepLocalTime: true });
    let endDateTimeVal = date
      .set({ hour: current.shiftEndHr, minute: current.shiftEndMin, second: 0, millisecond: 0 })
      .setZone(getTimeZoneFromSwaStation(station), { keepLocalTime: true });

    if (endDateTimeVal.toMillis() < startDateTimeVal.toMillis()) {
      // Shift crosses midnight, add one day to end time
      endDateTimeVal = endDateTimeVal.plus({ days: 1 });
    }

    return endDateTimeVal.toMillis() > now.toMillis();
  });
  return isAtLeastOneShiftInFutureEnd ? "" : AT_LEAST_ONE_SHIFT_IN_FUTURE_ERROR;
}

function validateShiftLength(segment, date, station, contract) {
  const contractMinShiftLength =
    contract === IAM_CONTRACT ? MIN_IAM_SHIFT_LENGTH_MINUTES : MIN_TWU_SHIFT_LENGTH_MINUTES;
  const contractMaxShiftLength =
    contract === IAM_CONTRACT ? MAX_IAM_SHIFT_LENGTH_MINUTES : MAX_TWU_SHIFT_LENGTH_MINUTES;

  const startDateTimeVal = setStartDateTime(date, segment.shiftStartHr, segment.shiftStartMin, station);
  let endDateTimeVal = setEndDateTime(date, segment.shiftEndHr, segment.shiftEndMin, station);

  if (endDateTimeVal.toMillis() < startDateTimeVal.toMillis()) {
    // Shift crosses midnight, add one day to end time
    endDateTimeVal = endDateTimeVal.plus({ days: 1 });
  }

  const timeDifference = getTimeDifferenceInMinutes(startDateTimeVal, endDateTimeVal);

  let error = "";
  if (timeDifference < contractMinShiftLength) {
    error = MIN_SHIFT_30_MINUTES_WARNING_MESSAGE;
  } else if (timeDifference > contractMaxShiftLength) {
    error =
      contract === IAM_CONTRACT
        ? MAX_SHIFT_10_AND_ONE_HALF_HOURS_WARNING_MESSAGE
        : MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
  }

  return error;
}

export function validateShiftSegmentComplete(segment) {
  if (validateShiftEndSegment(segment) !== "" && validateShiftStartSegment(segment) !== "") {
    return START_END_TIME_IS_REQUIRED_ERROR;
  }
  return "";
}

export function validateShiftStartSegment(segment) {
  if (segment.shiftStartHr === "" || segment.shiftStartMin === "") {
    return START_TIME_IS_REQUIRED;
  }
  return "";
}

export function validateShiftEndSegment(segment) {
  if (segment.shiftEndHr === "" || segment.shiftEndMin === "") {
    return END_TIME_IS_REQUIRED;
  }
  return "";
}

export function validateContinuousWithOrCalloutPreferenceSelected(
  continuousWithPreference,
  calloutHoursPreferences,
  calloutTimeOfDayPreferences
) {
  if (
    continuousWithPreference === "" &&
    calloutHoursPreferences.every((pref) => pref.preference === "") &&
    calloutTimeOfDayPreferences.every((pref) => pref.preference === "")
  ) {
    return AT_LEAST_ONE_PREFERENCE_MUST_BE_SELECTED_ERROR;
  }
  return "";
}

export function validateCalloutSection(calloutHoursPreferences, calloutTimeOfDayPreferences) {
  if (isCalloutSectionPartiallyFilled(calloutHoursPreferences, calloutTimeOfDayPreferences)) {
    if (calloutHoursPreferences.some((pref) => pref.preference !== "")) {
      return MUST_SELECT_AT_LEAST_ONE_TIME_OF_DAY_PREFERENCE_ERROR;
    } else if (calloutTimeOfDayPreferences.some((pref) => pref.preference !== "")) {
      return MUST_SELECT_AT_LEAST_ONE_HOURS_PREFERENCE_ERROR;
    }
  }
  return "";
}

export function validateCalloutSectionDuplication(calloutPreferences) {
  for (const calloutPref of calloutPreferences) {
    if (calloutPref.preference === "") continue;

    const items = calloutPreferences.filter((a) => a.preference === calloutPref.preference);
    //If more than one found, mark as duplicate
    if (items.length > 1) {
      //Apply error to all duplicates in the list
      calloutPreferences.forEach((pref) => {
        if (pref.preference === calloutPref.preference) {
          pref.error = PREFERENCE_SELECTED_DUPLICATION_ERROR;
        }
      });
    } else {
      calloutPref.error = "";
    }
  }

  return calloutPreferences;
}

export function isCalloutSectionPartiallyFilled(calloutHoursPreferences, calloutTimeOfDayPreferences) {
  const isCalloutHoursFilled = calloutHoursPreferences.every((pref) => pref.preference === "");
  const isCalloutTimeOfDayFilled = calloutTimeOfDayPreferences.every((pref) => pref.preference === "");
  return isCalloutHoursFilled || isCalloutTimeOfDayFilled;
}
